/**
 * FMEA scoring — the seeded failure modes with occurrence taken from the
 * fleet's own work-order history and detection credited for the monitoring
 * that now watches each component.
 *
 * ## Occurrence (O)
 *
 * Breakdown and corrective work orders created on or before `OEM_TODAY`,
 * whose component matches the row (`FmeaSeed.match`) on a machine of the
 * row's class, divided by the number of machines of that class that carry
 * the component and by the years the work-order history spans. The
 * per-machine-year rate is scored on a 1–10 ladder:
 *
 * | failures / machine / year | < 0.02 | < 0.05 | < 0.1 | < 0.2 | < 0.35 | < 0.5 | < 0.75 | < 1 | < 1.5 | ≥ 1.5 |
 * |---|---|---|---|---|---|---|---|---|---|---|
 * | O | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 |
 *
 * When no work order matches, the seed's `occurrence` stands and the row says
 * so (`occurrenceSource: 'seed'`).
 *
 * ## Detection (D)
 *
 * The seed's base detection, lowered by 2 when a TARP covers the class ×
 * component and by 1 when an SPC chart can (the component carries one of the
 * four tracked daily metrics), never below 1.
 *
 * RPN = S × O × D. Sorted by RPN, highest first.
 *
 * Engine layer (layer 2 of the 4-layer architecture): pure functions over
 * fetched data. No React, no clock, no randomness.
 */

import { FLEET, HEALTH } from '../../data/core.ts';
import type { FmeaRow } from '../../schema/operations.ts';
import type { WorkOrder } from '../../schema/work-order.ts';
import { OEM_TODAY } from '../oem-clock.ts';
import { FMEA_SEED, type FmeaSeed } from '../seed/fmea.ts';
import { TARPS } from '../seed/tarps.ts';
import { daysBetween, round } from './hash.ts';
import { loadReliabilityInputs } from './inputs.ts';
import { METRIC_COMPONENT } from './root-cause.ts';

/** What FMEA scoring reads. */
export interface FmeaCtx {
  readonly workOrders: readonly WorkOrder[];
}

/** A scored FMEA row, with the working behind O and D. */
export interface FmeaResult extends FmeaRow {
  /** Matching breakdown + corrective work orders. */
  failures: number;
  /** Machines of the class carrying the component. */
  machines: number;
  /** Years of work-order history the rate is over. */
  years: number;
  ratePerMachineYear: number;
  occurrenceSource: 'work-orders' | 'seed';
  /** The base detection before credits. */
  baseDetection: number;
  /** TARP id covering this row, if any. */
  tarpId: string | null;
  /** Whether a tracked daily metric (and so an SPC chart) watches this component. */
  spcCovered: boolean;
}

/** Occurrence ladder: upper bounds of failures / machine / year for O = 1…9. */
export const OCCURRENCE_LADDER = [0.02, 0.05, 0.1, 0.2, 0.35, 0.5, 0.75, 1, 1.5] as const;

/** Score a per-machine-year rate. */
export function occurrenceScore(rate: number): number {
  const i = OCCURRENCE_LADDER.findIndex((b) => rate < b);
  return i === -1 ? 10 : i + 1;
}

function scoreRow(seed: FmeaSeed, ctx: FmeaCtx, years: number): FmeaResult {
  const re = new RegExp(seed.match, 'i');
  const classMachines = new Set(FLEET.filter((f) => f.cls === seed.class).map((f) => f.machine_id));
  const carrying = new Set(HEALTH.filter((h) => classMachines.has(h.machine_id) && re.test(h.component)).map((h) => h.machine_id));
  const failures = ctx.workOrders.filter(
    (w) =>
      (w.wo_type === 'Breakdown' || w.wo_type === 'Corrective') &&
      w.created.slice(0, 10) <= OEM_TODAY &&
      classMachines.has(w.machine_id) &&
      re.test(w.component),
  ).length;
  const machines = carrying.size || classMachines.size;
  const rate = years > 0 && machines > 0 ? failures / machines / years : 0;
  const fromWos = failures > 0;
  const occurrence = fromWos ? occurrenceScore(rate) : seed.occurrence;

  const tarp = TARPS.find(
    (t) =>
      (t.class === seed.class || t.alsoClasses?.includes(seed.class)) &&
      (t.component === seed.component || re.test(t.component) || new RegExp(t.component, 'i').test(seed.component)),
  );
  const spcCovered = Object.values(METRIC_COMPONENT).some((src) => new RegExp(src, 'i').test(seed.component));
  const detection = Math.max(1, seed.detection - (tarp ? 2 : 0) - (spcCovered ? 1 : 0));

  const { match: _match, ...row } = seed;
  void _match;
  return {
    ...row,
    occurrence,
    detection,
    rpn: seed.severity * occurrence * detection,
    failures,
    machines,
    years: round(years, 2),
    ratePerMachineYear: round(rate, 3),
    occurrenceSource: fromWos ? 'work-orders' : 'seed',
    baseDetection: seed.detection,
    tarpId: tarp?.id ?? null,
    spcCovered,
  };
}

/** Every seeded failure mode, scored, highest RPN first. */
export function fmeaRows(ctx: FmeaCtx): FmeaResult[] {
  const first = ctx.workOrders.reduce((min, w) => (w.created < min ? w.created : min), `${OEM_TODAY}T99`);
  const years = daysBetween(first.slice(0, 10), OEM_TODAY) / 365.25;
  return FMEA_SEED.map((s) => scoreRow(s, ctx, years)).sort((a, b) => b.rpn - a.rpn || a.id.localeCompare(b.id));
}

/** Fetch work orders and score. */
export async function loadFmeaRows(): Promise<FmeaResult[]> {
  return fmeaRows(await loadReliabilityInputs());
}
